import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../../../components/structure/ModalLayout';
import FooterBar from '../../../components/structure/FooterBar';
import TextField from '../../../components/forms/TextField';
import Button from '../../../components/general/Button';
import PageError from '../../../components/general/PageError';
import StyledDropdown from '../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { color_shades_dark, color_shades_darkest, color_colors_ocean } from '../../../constants/colors';
import MedicalIntegrationConfigModel from '../../../models/organizations/MedicalIntegrationConfigModel';

const closeIcon = require('../../../content/images/close_icon.svg');

// Practice Management System options; values match what the integrations service expects
export const PRACTICE_MANAGEMENT_SYSTEMS = [
    { label: 'Denticon', value: 'Denticon' },
    { label: 'Dentrix Ascend', value: 'DentrixAscend' },
    { label: 'Cloud 9', value: 'Cloud9' },
    { label: 'Open Dental', value: 'OpenDental' },
    { label: 'Custom', value: 'Custom' }
];

const isKnownPMS = (value: string): boolean => {
    return PRACTICE_MANAGEMENT_SYSTEMS.some(pms => pms.value !== 'Custom' && pms.value === value);
};

// Open saved JSON pretty-printed; leave it untouched if it doesn't parse
const withFormattedJson = (config: MedicalIntegrationConfigModel): MedicalIntegrationConfigModel => {
    try {
        return { ...config, configuration: JSON.stringify(JSON.parse(config.configuration), null, 2) };
    } catch (e) {
        return { ...config };
    }
};

interface MedicalIntegrationConfigModalProps {
    config: MedicalIntegrationConfigModel;
    isSaving: boolean;
    errors: string[];
    onClose: () => void;
    onSave: (config: MedicalIntegrationConfigModel) => void;
}

const MedicalIntegrationConfigModal: React.FC<MedicalIntegrationConfigModalProps> = ({ config, isSaving, errors, onClose, onSave }) => {
    const [form, setForm] = useState<MedicalIntegrationConfigModel>(withFormattedJson(config));
    const [isCustomPMS, setIsCustomPMS] = useState(!!config.integrationType && !isKnownPMS(config.integrationType));
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        setForm(withFormattedJson(config));
        setIsCustomPMS(!!config.integrationType && !isKnownPMS(config.integrationType));
        setValidationErrors([]);
    }, [config]);

    const isNewConfig = !config.id;

    const formatJson = () => {
        try {
            setForm({ ...form, configuration: JSON.stringify(JSON.parse(form.configuration), null, 2) });
            setValidationErrors([]);
        } catch (e) {
            setValidationErrors(['Cannot format: Configuration is not valid JSON']);
        }
    };

    const handleSave = () => {
        const nextErrors: string[] = [];
        if (!form.clientName.trim()) nextErrors.push('Client name is required');
        if (!form.integrationType.trim()) nextErrors.push('Practice management system is required');
        try {
            JSON.parse(form.configuration);
        } catch (e) {
            nextErrors.push('Configuration must be valid JSON');
        }
        setValidationErrors(nextErrors);
        if (nextErrors.length === 0) {
            onSave({ ...form, clientName: form.clientName.trim(), integrationType: form.integrationType.trim() });
        }
    };

    return (
        <ModalLayout className={modalStyle} isVisible={true} onClose={onClose} zIndex={1010}>
            <div className={containerStyle}>
                <div className="header-wrapper">
                    <h2>{isNewConfig ? 'Add PMS Configuration' : `Edit ${config.clientName}`}</h2>
                    <Button
                        className="close-icon"
                        themes={['end-tight', 'icon']}
                        icon={closeIcon}
                        type="button"
                        onClick={onClose}
                    />
                </div>

                <div className="form-container">
                    <TextField
                        name="clientName"
                        value={form.clientName}
                        placeholder="e.g. Smile Doctors"
                        label="Client Name"
                        onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                        disabled={isSaving}
                        useForm={false}
                    />

                    <StyledDropdown
                        label="Practice Management System"
                        fieldName="integrationType"
                        placeholder="Select practice management system"
                        options={PRACTICE_MANAGEMENT_SYSTEMS}
                        value={isCustomPMS ? 'Custom' : form.integrationType}
                        handleOnChange={(fieldName, value) => {
                            const isCustom = value === 'Custom';
                            setIsCustomPMS(isCustom);
                            // Custom clears the type so a new name can be typed
                            setForm({ ...form, integrationType: isCustom ? '' : value as string });
                        }}
                        disabled={isSaving}
                    />
                    {isCustomPMS && (
                        <TextField
                            name="customIntegrationType"
                            value={form.integrationType}
                            placeholder="Enter custom practice management system"
                            label="Custom System Name"
                            onChange={(e) => setForm({ ...form, integrationType: e.target.value })}
                            disabled={isSaving}
                            useForm={false}
                        />
                    )}

                    <div className="json-label-row">
                        <label htmlFor="medical-config-json">Configuration (JSON)</label>
                        <button type="button" className="text-link" onClick={formatJson} disabled={isSaving}>Format JSON</button>
                    </div>
                    {/* Plain textarea: TextAreaField treats {braces} as reference variables */}
                    <textarea
                        id="medical-config-json"
                        className="json-field"
                        name="configuration"
                        value={form.configuration}
                        placeholder='{"ClientId": "..."}'
                        onChange={(e) => setForm({ ...form, configuration: e.target.value })}
                        rows={12}
                        disabled={isSaving}
                        spellCheck={false}
                    />

                    <PageError errors={[...validationErrors, ...errors]} />
                </div>

                <FooterBar>
                    <Button
                        themes={['primary', 'wide']}
                        onClick={handleSave}
                        text={isSaving ? 'Saving...' : (isNewConfig ? 'Create' : 'Save')}
                        disabled={isSaving}
                        type="button"
                    />
                    <Button
                        themes={['white', 'end']}
                        onClick={onClose}
                        text="Cancel"
                        disabled={isSaving}
                        type="button"
                    />
                </FooterBar>
            </div>
        </ModalLayout>
    );
};

const modalStyle = css`
    width: 720px;
    max-width: 92vw;
    height: auto;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
`;

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 90vh;
    overflow: hidden;

    .header-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 32px 16px 32px;
        border-bottom: solid 1px ${color_shades_dark};
        flex-shrink: 0;

        h2 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: ${color_shades_darkest};
        }

        .close-icon {
            cursor: pointer;
        }
    }

    .form-container {
        padding: 32px;
        padding-bottom: 16px;
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }

    .json-label-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 16px;
        font-family: Muli;
        font-size: 14px;
        color: ${color_shades_darkest};
    }

    .json-field {
        width: 100%;
        box-sizing: border-box;
        margin: 8px 0 16px 0;
        padding: 12px 16px;
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
        font-size: 12px;
        color: ${color_shades_darkest};
        resize: vertical;
        outline: none;

        &:focus {
            border-color: ${color_colors_ocean};
        }

        &:disabled {
            background: #f5f5f5;
        }
    }

    .text-link {
        background: none;
        border: none;
        padding: 0;
        color: ${color_colors_ocean};
        font-family: Muli;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        border-bottom: 1px dashed ${color_colors_ocean};

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
`;

export default MedicalIntegrationConfigModal;
